import React, { FC, useState, lazy, Suspense } from 'react';
import { Box, Button, IconButton } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { useExperiments } from '@wix/fe-essentials-standalone';
import {
  ChangeOrder,
  ContentFilter,
  HeartSmall,
} from '@wix/wix-ui-icons-common';
import MobileFilterModal from '../TemplateMobileFilters';
import { useTemplateGalleryFilters } from '@components/Hooks/useTemplateGalleryFilters/useTemplateGalleryFilters';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import TemplateSearchField from '../TemplateSearchField';
import useTemplateSearchField from '../TemplateSearchField/useTemplateSearchField';
import SearchTermTag from '../TemplateGallery/FilterTags/SearchTermTag';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import {
  ENABLE_TEMPLATE_FAVORITES_SPEC,
  TEMPLATES_GALLERY_ENTRY,
} from '@utils/constants';
import { FAVORITES_BI_CLICK_TYPES } from '@components/Templates/Favorites/useFavoritesBi';
import { templatesFavoritesPath } from '@components/Hooks/useRouter';
import { redirectToLoginWithFavoritesPage } from '@utils/urlUtils';
import s from './MobileActions.module.scss';

const MobileSortModalLayout = lazy(() =>
  import('../MobileSortModalLayout').then((module) => ({
    default: module.default,
  })),
);

export const dataHooks = {
  stickyContainer: 'mobile-actions-sticky-container',
  actionsContainer: 'mobile-actions-buttons-container',
  searchContainer: 'mobile-actions-search-container',
  filterButton: 'mobile-actions-filter-button',
  sortButton: 'mobile-actions-sort-button',
} as const;

const MobileActions: FC = () => {
  const {
    state: { appStatus, numberOfResults },
  } = useTemplatesContext();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { experiments } = useExperiments({ readOnly: true });
  const { userDetails } = useHeaderContext();
  const showFavoritesButton = experiments.enabled(
    ENABLE_TEMPLATE_FAVORITES_SPEC,
  );
  const [openSortMenu, setOpenSortMenu] = useState<boolean>(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const { totalFilterCount } = useTemplateGalleryFilters();
  const { logBiClick } = useTemplatesBi({ is_mobile: true });
  const {
    setSearchInputValue,
    searchInputValue,
    onSelect,
    clearSearchInputValue,
    onSearch,
  } = useTemplateSearchField();

  const showSearchTermTag = appStatus === 'initialized' && numberOfResults > 0;
  const isExpanded = isSearchFocused || searchInputValue;
  const searchContainerClass = isExpanded ? s.searchExpand : '';
  const componentMinimizeClass = isExpanded ? s.searchExpandOtherActions : '';

  return (
    <>
      <Box
        className={s.sticky}
        direction="vertical"
        backgroundColor="white"
        dataHook={dataHooks.stickyContainer}
      >
        <Box
          align="center"
          verticalAlign="middle"
          gap="SP2"
          direction="horizontal"
          padding="SP2 SP4"
          borderBottom="1px solid var(--wsr-color-D60, #DFE5EB)"
          dataHook={dataHooks.actionsContainer}
        >
          <Box
            className={`${searchContainerClass} ${
              !isExpanded ? s.searchIconBorder : ''
            }`}
            dataHook={dataHooks.searchContainer}
          >
            <TemplateSearchField
              expandable
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e?.target?.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              onClear={clearSearchInputValue}
              onEnterPressed={(e) => {
                onSearch(e);
                clearSearchInputValue();
              }}
              onSelect={(e) => onSelect(e)}
            />
          </Box>
          {showFavoritesButton && (
            <Box
              flexShrink={0}
              className={`${s.favoritesButton} ${componentMinimizeClass}`}
            >
              <IconButton
                priority="secondary"
                size="medium"
                onClick={() => {
                  logBiClick({
                    click_type: FAVORITES_BI_CLICK_TYPES.favorites,
                    entry: TEMPLATES_GALLERY_ENTRY,
                  });
                  if (Boolean(userDetails?.email)) {
                    window.location.assign(templatesFavoritesPath);
                  } else {
                    redirectToLoginWithFavoritesPage(language);
                  }
                }}
                dataHook="mobile-actions-favorites-button"
              >
                <HeartSmall />
              </IconButton>
            </Box>
          )}
          <Button
            priority="secondary"
            size="medium"
            fullWidth={true}
            onClick={() => {
              logBiClick({ click_type: 'Filter' });
              setIsFilterMenuOpen((current) => !current);
            }}
            className={componentMinimizeClass}
            prefixIcon={<ContentFilter />}
            dataHook={dataHooks.filterButton}
          >
            {t('marketplace.templates-page.mobile.filter-cta', {
              count: totalFilterCount,
            })}
          </Button>
          <Button
            priority="secondary"
            size="medium"
            fullWidth={true}
            className={componentMinimizeClass}
            prefixIcon={<ChangeOrder />}
            onClick={() => {
              logBiClick({ click_type: 'Sort' });
              setOpenSortMenu(true);
            }}
            dataHook={dataHooks.sortButton}
          >
            {t('mobile-sort-button-cta')}
          </Button>
        </Box>
        {showSearchTermTag && <SearchTermTag className={s.searchTermPadding} />}
      </Box>
      {openSortMenu && (
        <Suspense fallback="">
          <MobileSortModalLayout setOpenSortMenu={setOpenSortMenu} />
        </Suspense>
      )}
      {isFilterMenuOpen && (
        <MobileFilterModal
          isOpen={isFilterMenuOpen}
          setIsFilterMenuOpen={setIsFilterMenuOpen}
        />
      )}
    </>
  );
};

export default MobileActions;
